import { Component, ComponentInterface, Element, h, Host, JSX, Prop } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { WorkbenchTheme, getDataOnFromTheme } from '../../shared/themes';

/**
 * The Bar loader notifies users that a current process or action needs more time to progress. And informs the user
 * (approximately) how long it will take.
 */
@Component({
  tag: 'wb-bar-loader',
  styleUrl: 'bar-loader.scss',
  shadow: true,
})
export class BarLoaderComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** A percentage between 0 and 100 or 'indeterminate' */
  @Prop({ reflect: true })
  public value: number | 'indeterminate' = 0;

  /** If present, displays compact variant */
  @Prop({ reflect: true })
  public compact: boolean = false;

  /** If present, displays variant with border radius applied */
  @Prop({ reflect: true })
  public rounded: boolean = false;

  /** If present, hides the percentage */
  @Prop({ reflect: true })
  public hidePercentage: boolean = false;

  /** If present, positions label and percentage on the left and right side of the loading bar */
  @Prop({ reflect: true })
  public sideLabel: boolean = false;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Optional label describing the loading process */
  @Prop({ reflect: true })
  public label: string = '';

  /** aria-label for the progress element */
  @Prop({ reflect: true })
  public progressAriaLabel: string = '';

  /** aria-valuetext for indeterminate progress element */
  @Prop({ reflect: true })
  public indeterminateAriaValueText: string = 'processing';

  private TagNames = transformTagNames(this, 'wb-bar-loader', ['wb-text', 'wb-progress']);

  public render(): JSX.Element {
    const { WbText, WbProgress } = this.TagNames;

    return (
      <Host class="wb-bar-loader" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <div class={{ inner: true, 'has-no-text': this.hidePercentage && !this.label.length }}>
          {this.label && (
            <WbText class="label" size="l" static ellipsis>
              {this.label}
            </WbText>
          )}
          <WbProgress value={this.value} compact={this.compact} rounded={this.rounded} progressAriaLabel={this.progressAriaLabel} indeterminateAriaValueText={this.indeterminateAriaValueText} />
          {this.hidePercentage || this.value === 'indeterminate' ? null : ( //
            <WbText class="percentage" size="l" static>{`${Math.round(this.value)}%`}</WbText>
          )}
        </div>
      </Host>
    );
  }
}
